import React, { useState } from 'react';

import { DialogActions, Typography } from '@mui/material';
import { useMountedState } from 'react-use';
/*
 * Imported directly rather than through `src/components`, which re-exports
 * this file: going back through the barrel is a cycle, and it pulls the whole
 * component graph in behind a dialog that needs three pieces of it.
 */
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import type { AlineDialogProps } from 'src/components/generic/AlineDialog';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  deleteButtonWrapper: {
    marginLeft: theme.spacing(1),
  },
  dialog: {
    width: 566,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  iconWrapper: {
    marginRight: theme.spacing(2),
  },
  textContainer: {
    alignItems: 'flex-start',
    display: 'flex',
  },
}));

export enum ConfirmationDialogVariant {
  Error,
  Warn,
  Info,
}

export interface ConfirmationDialogProps extends Omit<React.HTMLAttributes<HTMLElement>, 'role'> {
  message: React.ReactNode;
  onClickPrimary: (event: React.MouseEvent) => void;
  onClose: () => void;
  open: boolean;
  title: string;
  children?: React.ReactNode;
  disableAltClose?: boolean;
  hideIcon?: boolean;
  onClickSecondary?: (event: React.MouseEvent) => void;
  onExit?: () => void;
  primaryDataCy?: string;
  primaryDisabledReason?: string;
  primaryText?: string;
  secondaryDataCy?: string;
  secondaryText?: string;
  showSecondary?: boolean;
  TransitionProps?: AlineDialogProps['TransitionProps'];
  variant?: ConfirmationDialogVariant;
}

export function ConfirmationDialog(props: ConfirmationDialogProps): JSX.Element {
  const {
    message,
    onClickPrimary,
    open,
    primaryText = 'Delete',
    secondaryText = 'Cancel',
    onClose,
    secondaryDataCy,
    title,
    disableAltClose = false,
    hideIcon = false,
    onClickSecondary = onClose,
    variant = ConfirmationDialogVariant.Warn,
    children,
    primaryDisabledReason = '',
    onExit,
    showSecondary = true,
    primaryDataCy = 'submit-button',
    TransitionProps,
    ...rest
  } = props;
  const { classes } = useStyles();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const isMounted = useMountedState();

  // errorIconWrapper preserves 24px size of ErrorIcon SVG.
  return (
    <AlineDialog
      {...rest}
      TransitionProps={{
        ...TransitionProps,
        onExit: node => {
          onExit?.();
          TransitionProps?.onExit?.(node);
        },
      }}
      classes={{ paper: classes.dialog }}
      disableAltClose={disableAltClose}
      open={open}
      title={title}
      onClose={onClose}
    >
      <div className={classes.textContainer}>
        {!hideIcon && (
          <div className={classes.iconWrapper}>
            <DialogIcon variant={variant} />
          </div>
        )}
        <Typography variant="body1">{message}</Typography>
      </div>
      {children}
      <DialogActions>
        {showSecondary && (
          <AlineButton
            color="primary"
            data-cy={secondaryDataCy}
            disabled={submitting}
            variant="outlined"
            onClick={onClickSecondary}
          >
            {secondaryText}
          </AlineButton>
        )}
        <AlineTooltip arrow placement="top" title={primaryDisabledReason}>
          <div className={classes.deleteButtonWrapper}>
            <AlineButton
              color="primary"
              data-cy={primaryDataCy}
              disabled={!!primaryDisabledReason || submitting}
              loading={submitting}
              variant="contained"
              onClick={async event => {
                setSubmitting(true);

                await onClickPrimary(event);
                if (!isMounted()) return;

                setSubmitting(false);
                onClose();
              }}
            >
              {primaryText}
            </AlineButton>
          </div>
        </AlineTooltip>
      </DialogActions>
    </AlineDialog>
  );
}

interface DialogIconProps {
  variant: ConfirmationDialogVariant;
}

function DialogIcon({ variant }: DialogIconProps): JSX.Element {
  const { classes } = useStyles();

  switch (variant) {
    case ConfirmationDialogVariant.Error:
      return <ErrorIcon className={classes.errorIcon} />;
    case ConfirmationDialogVariant.Warn:
      return <WarningIcon />;
    case ConfirmationDialogVariant.Info:
      return <InfoIcon />;
  }
}
